import React, { useState, useCallback, useMemo } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Text, TextInput, Button, Card, Divider } from 'react-native-paper';
import { pickPhotos, toFormFile } from '../Common/photoPicker';
import Icon from 'react-native-vector-icons/FontAwesome';
import moment from 'moment';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import { useGetAmCategoriesQuery } from '../redux/services/amcategoryApi';
import { useGetSuppliersQuery } from '../redux/services/stmsupplierApi';
import { useInsertAmPurchaseMutation, useUpdateAmPurchaseMutation, type AmPurchase, type AmPurchaseDetail, type AmPurchaseMode } from '../redux/services/ampurchaseApi';

import { useGetDepartmentsQuery } from '../redux/services/emmdepartmentApi';
import { useUploadAmDocumentMutation } from '../redux/services/amdocumentApi';
import { useLazyGetAmNextCodeQuery } from '../redux/services/amassetApi';
import AmPhoto from '../Components/AmPhoto';
import ProductPicker from '../Components/ProductPicker';
import type { AmProduct } from '../redux/services/amproductApi';
import { ShowToast } from '../Common/common';
import { usePhoneticText } from '../utils/transliterate/usePhoneticInput';
import T from '../Components/T';
import { tp } from '../utils/translateText';
const AmAssetPurchaseEntry = ({ navigation, route }: any) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const company = gdata?.company;
  const userId = gdata?.user;

  const editingId = route?.params?.purchaseId;

  const ph = usePhoneticText();
  const [header, setHeader] = useState<Partial<AmPurchase>>({
    ap_company: company,
    ap_date: moment().format('YYYY-MM-DD'),
    ap_supplier: null,
    ap_invoiceno: '',
    ap_amount: 0,
    ap_type: 'New',
    ap_notes: '',
    ap_active: '1',
    ap_details: [],
    ap_modes: [{ pm_mode: 1, pm_amt: 0 }] // Default payment mode
  });

  const [details, setDetails] = useState<AmPurchaseDetail[]>([]);

  const { data: categories = [] } = useGetAmCategoriesQuery(company || '', { skip: !company });
  const { data: suppliers = [] } = useGetSuppliersQuery(company || '', { skip: !company });

  const { data: departments = [] } = useGetDepartmentsQuery(company || '', { skip: !company });
  const [uploadDocument] = useUploadAmDocumentMutation();
  const [getNextCode] = useLazyGetAmNextCodeQuery();
  const [supplierMenu, setSupplierMenu] = useState(false);
  const [deptMenuFor, setDeptMenuFor] = useState<number | null>(null);

  const [insertPurchase, { isLoading: isSaving }] = useInsertAmPurchaseMutation();
  const [updatePurchase, { isLoading: isUpdating }] = useUpdateAmPurchaseMutation();

  const handleAddDetailRow = useCallback(() => {
    const newRow: AmPurchaseDetail = {
      pd_name: '',
      pd_category: null,
      pd_code: '',
      pd_qty: 1,
      pd_rate: 0,
      pd_amount: 0
    };
    setDetails([...details, newRow]);
  }, [details]);

  // Photos picked per line are kept on the row (as `_photos`) until the purchase is saved
  const addPhotos = async (index: number, source: 'camera' | 'library') => {
    try {
      const picked = await pickPhotos(source);
      if (!picked.length) return;
      setDetails(prev => prev.map((d, i) => (i === index ? { ...d, _photos: [...((d as any)._photos || []), ...picked] } : d)));
    } catch (e: any) {
      ShowToast(e?.message || 'Could not open the camera', 'error');
    }
  };

  const removePhoto = (index: number, photoIdx: number) =>
    setDetails(prev =>
      prev.map((d, i) => (i === index ? { ...d, _photos: ((d as any)._photos || []).filter((_: any, k: number) => k !== photoIdx) } : d))
    );

  const takePhoto = (index: number) => addPhotos(index, 'camera');
  const pickPhoto = (index: number) => addPhotos(index, 'library');

  // Same defaults as the web purchase line: code/name/category/rate, depreciation and warranty from the product
  const handleProductSelect = (index: number, p: AmProduct | null) => {
    setDetails(prev => {
      const updated = [...prev];
      const row: any = { ...updated[index], pd_product: p ? Number(p.pt_id) : null };
      if (p) {
        row.pd_code = p.pt_code;
        row.pd_name = p.pt_name;
        if (p.pt_category) row.pd_category = Number(p.pt_category);
        if (p.pt_cost != null && p.pt_cost > 0) {
          row.pd_rate = Number(p.pt_cost);
          row.pd_amount = (row.pd_qty || 1) * Number(p.pt_cost);
        }
        if (p.pt_depmethod) row.pd_depmethod = p.pt_depmethod;
        if (p.pt_deprate != null) row.pd_deprate = Number(p.pt_deprate);
        if (p.pt_usefullife != null) row.pd_usefullife = Number(p.pt_usefullife);
        if (p.pt_salvagevalue != null) row.pd_salvagevalue = Number(p.pt_salvagevalue);
        if (p.pt_warranty_months != null && p.pt_warranty_months > 0) {
          row.pd_wr_enabled = '1';
          row.pd_wr_startdate = header.ap_date;
          row.pd_wr_enddate = moment(header.ap_date).add(p.pt_warranty_months, 'months').format('YYYY-MM-DD');
        }
      }
      updated[index] = row;
      if (p) {
        // The product code is the default asset code; suggest the next free one if it is already used
        const taken = prev.filter((_d, i) => i !== index).map(d => d.pd_code).filter(Boolean).join(',');
        getNextCode({ code: p.pt_code, taken })
          .unwrap()
          .then(r => {
            if (r.changed) {
              setDetails(cur => cur.map((d, i) => (i === index && d.pd_code === p.pt_code ? { ...d, pd_code: r.code } : d)));
            }
          })
          .catch(() => {});
      }
      setHeader(h => ({ ...h, ap_amount: updated.reduce((sum, d) => sum + (d.pd_amount || 0), 0) }));
      return updated;
    });
  };

  const handleUpdateDetail = (index: number, field: keyof AmPurchaseDetail, value: any) => {
    const updated = [...details];
    updated[index] = { ...updated[index], [field]: value };

    // Recalculate amount
    if (field === 'pd_qty' || field === 'pd_rate') {
      const qty = field === 'pd_qty' ? value : updated[index].pd_qty;
      const rate = field === 'pd_rate' ? value : updated[index].pd_rate;
      updated[index].pd_amount = (qty || 0) * (rate || 0);
    }

    setDetails(updated);

    // Update header amount
    const totalAmount = updated.reduce((sum, d) => sum + (d.pd_amount || 0), 0);
    setHeader({ ...header, ap_amount: totalAmount });
  };

  const handleRemoveDetail = (index: number) => {
    const updated = details.filter((_, i) => i !== index);
    setDetails(updated);
    const totalAmount = updated.reduce((sum, d) => sum + (d.pd_amount || 0), 0);
    setHeader({ ...header, ap_amount: totalAmount });
  };

  const handleSave = async () => {
    if (!header.ap_supplier) {
      ShowToast('Please select a supplier', 'error');
      return;
    }

    if (details.length === 0 || details.every(d => !d.pd_code)) {
      ShowToast('Please add at least one asset line item', 'error');
      return;
    }

    const lines = details.filter(d => d.pd_code && d.pd_name);
    const purchase: AmPurchase = {
      ...header,
      ap_details: lines.map(l => {
        const { _photos, ...rest } = l as any;
        return rest;
      }),
      ap_modes: header.ap_modes || [{ pm_mode: 1, pm_amt: header.ap_amount }]
    } as AmPurchase;

    try {
      let apId: number = editingId;
      let renamed: { from: string; to: string }[] = [];
      if (editingId) {
        purchase.ap_id = editingId;
        const res = await updatePurchase(purchase).unwrap();
        renamed = res.ap_renamed || [];
      } else {
        const res = await insertPurchase(purchase).unwrap();
        apId = res.ap_id;
        renamed = res.ap_renamed || [];
      }
      if (renamed.length) {
        ShowToast('Asset code already in use, saved as: ' + renamed.map(r => `${r.from} -> ${r.to}`).join(', '));
      }

      // Upload photos against the saved purchase line (line number = position among saved lines)
      let failed = 0;
      for (let i = 0; i < lines.length; i++) {
        for (const photo of (lines[i] as any)._photos || []) {
          const fd = new FormData();
          fd.append('file', toFormFile(photo));
          fd.append('ed_reftype', 'Purchase');
          fd.append('ed_refid', String(apId));
          fd.append('ed_refline', String(i + 1));
          fd.append('category', 'Photo');
          fd.append('title', lines[i].pd_name);
          try {
            await uploadDocument(fd).unwrap();
          } catch (e) {
            failed++;
          }
        }
      }
      if (failed) {
        ShowToast(`Saved, but ${failed} photo(s) failed to upload`, 'error');
      } else {
        ShowToast(editingId ? 'Purchase updated successfully' : 'Purchase created successfully');
      }

      if (!editingId) {
        setHeader({
          ap_company: company,
          ap_date: moment().format('YYYY-MM-DD'),
          ap_supplier: null,
          ap_invoiceno: '',
          ap_amount: 0,
          ap_type: 'New',
          ap_notes: '',
          ap_active: '1',
          ap_details: [],
          ap_modes: [{ pm_mode: 1, pm_amt: 0 }]
        });
        setDetails([]);
      }
      navigation.goBack();
    } catch (err: any) {
      ShowToast(err?.data?.message || 'Failed to save purchase', 'error');
    }
  };

  const suppliersOptions = suppliers.map(s => ({ label: s.sp_name, value: s.sp_id }));
  const categoriesOptions = categories.map(c => ({ label: c.ct_name, value: c.ct_id }));

  return (
    <ScrollView style={styles.container}>
      <Card style={styles.section}>
        <Card.Title title="Purchase Header" />
        <View style={styles.formGroup}>
          <Text style={styles.label}><T>Date</T></Text>
          <TextInput
            value={header.ap_date}
            onChangeText={(v) => setHeader({ ...header, ap_date: v })}
            mode="outlined"
            placeholder={tp("YYYY-MM-DD")}
            style={styles.input}
          />
        </View>

        <View style={styles.formGroup}>
          <Text style={styles.label}><T>Supplier *</T></Text>
          <TouchableOpacity style={styles.selectInput} onPress={() => setSupplierMenu(!supplierMenu)}>
            <Text>{suppliers.find(s => s.sp_id === header.ap_supplier)?.sp_name || 'Select Supplier'}</Text>
          </TouchableOpacity>
          {supplierMenu && (
            <ScrollView style={styles.dropdown} nestedScrollEnabled>
              {suppliers.map(s => (
                <TouchableOpacity
                  key={String(s.sp_id)}
                  style={styles.dropdownItem}
                  onPress={() => {
                    setHeader({ ...header, ap_supplier: Number(s.sp_id) });
                    setSupplierMenu(false);
                  }}
                >
                  <Text>{s.sp_name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}
        </View>

        <View style={styles.formGroup}>
          <Text style={styles.label}><T>Invoice No</T></Text>
          <TextInput
            value={header.ap_invoiceno || ''}
            onChangeText={(v) => setHeader({ ...header, ap_invoiceno: v })}
            mode="outlined"
            placeholder={tp("Invoice number")}
            style={styles.input}
          />
        </View>

        <View style={styles.formGroup}>
          <Text style={styles.label}><T>Type</T></Text>
          <TouchableOpacity style={styles.selectInput}>
            <Text>{header.ap_type}</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.formGroup}>
          <Text style={styles.label}><T>Notes</T></Text>
          <TextInput
            value={header.ap_notes || ''}
            onChangeText={(v) => setHeader({ ...header, ap_notes: ph.onChange(v) })}
            onBlur={() => setHeader({ ...header, ap_notes: ph.onBlur(header.ap_notes || '') })}
            mode="outlined"
            placeholder={tp("Notes")}
            multiline
            numberOfLines={3}
            style={styles.input}
          />
        </View>
      </Card>

      <Card style={styles.section}>
        <Card.Title title={`Asset Lines (${details.length})`} />

        {details.map((detail, idx) => (
          <View key={idx}>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}><T>Item</T> {idx + 1}</Text>
              <TouchableOpacity onPress={() => handleRemoveDetail(idx)}>
                <Icon name="trash" size={18} color="#d32f2f" />
              </TouchableOpacity>
            </View>

            <ProductPicker value={detail.pd_product} onSelect={p => handleProductSelect(idx, p)} />

            <TextInput
              placeholder={tp("Asset Name *")}
              value={detail.pd_name}
              onChangeText={(v) => handleUpdateDetail(idx, 'pd_name', ph.onChange(v))}
              onBlur={() => handleUpdateDetail(idx, 'pd_name', ph.onBlur(detail.pd_name || ''))}
              mode="outlined"
              style={styles.detailInput}
            />

            <TextInput
              placeholder={tp("Asset Code *")}
              value={detail.pd_code}
              onChangeText={(v) => handleUpdateDetail(idx, 'pd_code', v)}
              mode="outlined"
              style={styles.detailInput}
            />

            <TextInput
              placeholder={tp("Serial No. (blank = one record with the quantity)")}
              value={detail.pd_serialno || ''}
              onChangeText={(v) => handleUpdateDetail(idx, 'pd_serialno', v)}
              mode="outlined"
              style={styles.detailInput}
            />

            <View style={styles.rowInputs}>
              <TextInput
                placeholder={tp("Qty")}
                value={String(detail.pd_qty || 1)}
                onChangeText={(v) => handleUpdateDetail(idx, 'pd_qty', parseInt(v) || 1)}
                mode="outlined"
                keyboardType="numeric"
                style={[styles.detailInput, styles.halfInput]}
              />
              <TextInput
                placeholder={tp("Rate")}
                value={String(detail.pd_rate || 0)}
                onChangeText={(v) => handleUpdateDetail(idx, 'pd_rate', parseFloat(v) || 0)}
                mode="outlined"
                keyboardType="decimal-pad"
                style={[styles.detailInput, styles.halfInput]}
              />
            </View>

            <TouchableOpacity
              style={[styles.selectInput, styles.detailInput]}
              onPress={() => setDeptMenuFor(deptMenuFor === idx ? null : idx)}
            >
              <Text>
                {departments.find(dp => Number(dp.dp_id) === Number(detail.pd_department))?.dp_name || 'Department (optional)'}
              </Text>
            </TouchableOpacity>
            {deptMenuFor === idx && (
              <ScrollView style={styles.dropdown} nestedScrollEnabled>
                <TouchableOpacity
                  style={styles.dropdownItem}
                  onPress={() => { handleUpdateDetail(idx, 'pd_department', null); setDeptMenuFor(null); }}
                >
                  <Text><T>None</T></Text>
                </TouchableOpacity>
                {departments.map(dp => (
                  <TouchableOpacity
                    key={String(dp.dp_id)}
                    style={styles.dropdownItem}
                    onPress={() => { handleUpdateDetail(idx, 'pd_department', Number(dp.dp_id)); setDeptMenuFor(null); }}
                  >
                    <Text>{dp.dp_name}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}

            <View style={styles.photoRow}>
              {((detail as any)._photos || []).map((ph: any, k: number) => (
                <AmPhoto key={`${ph.uri}-${k}`} uri={ph.uri} onRemove={() => removePhoto(idx, k)} />
              ))}
              <TouchableOpacity style={styles.photoBtn} onPress={() => takePhoto(idx)}>
                <Icon name="camera" size={18} color="#2E7D32" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.photoBtn} onPress={() => pickPhoto(idx)}>
                <Icon name="image" size={18} color="#2E7D32" />
              </TouchableOpacity>
            </View>

            <View style={styles.amountRow}>
              <Text><T>Amount: ₹</T>{((detail.pd_amount || 0).toFixed(2))}</Text>
            </View>

            <Divider style={styles.detailDivider} />
          </View>
        ))}

        <Button mode="outlined" onPress={handleAddDetailRow} style={styles.addButton}>
          Add Line Item
        </Button>
      </Card>

      <Card style={styles.section}>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}><T>Total Amount:</T></Text>
          <Text style={styles.summaryValue}>₹{(header.ap_amount || 0).toFixed(2)}</Text>
        </View>
      </Card>

      <View style={styles.buttonGroup}>
        <Button
          mode="contained"
          onPress={handleSave}
          loading={isSaving || isUpdating}
          disabled={isSaving || isUpdating}
          style={styles.saveButton}
        >
          Save Purchase
        </Button>
        <Button mode="outlined" onPress={() => navigation.goBack()}>
          Cancel
        </Button>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 10
  },
  section: {
    marginBottom: 15,
    padding: 15
  },
  formGroup: {
    marginBottom: 15
  },
  label: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#333'
  },
  input: {
    marginBottom: 5
  },
  selectInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    padding: 12,
    backgroundColor: '#fafafa'
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10
  },
  detailLabel: {
    fontWeight: 'bold',
    fontSize: 12
  },
  detailInput: {
    marginBottom: 10,
    fontSize: 13
  },
  rowInputs: {
    flexDirection: 'row',
    gap: 10
  },
  halfInput: {
    flex: 1
  },
  dropdown: {
    maxHeight: 180,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    backgroundColor: '#fff',
    marginTop: 4,
    marginBottom: 10
  },
  dropdownItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee'
  },
  photoRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10
  },
  photoBtn: {
    width: 64,
    height: 64,
    borderRadius: 6,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#2E7D32',
    alignItems: 'center',
    justifyContent: 'center'
  },
  amountRow: {
    padding: 10,
    backgroundColor: '#f0f0f0',
    borderRadius: 4,
    marginBottom: 10
  },
  detailDivider: {
    marginVertical: 10
  },
  addButton: {
    marginTop: 10
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    backgroundColor: '#f9f9f9'
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: 'bold'
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2E7D32'
  },
  buttonGroup: {
    gap: 10,
    marginBottom: 30
  },
  saveButton: {
    marginBottom: 10
  }
});

export default AmAssetPurchaseEntry;
